import { CartUpdateEvent, ThemeEvents } from "@theme/events";
import { formatShopifyMoney, fetchConfig } from "@theme/utilities";

class FbtBundle extends HTMLElement {
  connectedCallback() {
    this.listEl = this.querySelector(".fbt-list");
    this.totalEl = this.querySelector("[data-fbt-total]");
    this.totalCompareEl = this.querySelector("[data-fbt-total-compare]");
    this.ctaEl = this.querySelector("[data-fbt-add-all]");
    this.ctaLabelEl = this.querySelector(".fbt-cta__label");
    this.errorEl = this.querySelector("[data-fbt-error]");
    this.savingsEl = this.querySelector("[data-fbt-savings]");
    this.savingsAmountEl = this.querySelector("[data-fbt-savings-amount]");
    this.promoFillEl = this.querySelector("[data-fbt-promo-fill]");
    this.promoCountEl = this.querySelector("[data-fbt-promo-count]");
    this.promoStatusEl = this.querySelector("[data-fbt-promo-progress]");
    this.promoNodes = Array.from(this.querySelectorAll(".fbt-promo__node"));
    this.cartFreeEl = this.querySelector("[data-fbt-cart-free]");
    this.cartFreeListEl = this.querySelector("[data-fbt-cart-free-list]");
    this.loadMoreBtn = this.querySelector("[data-fbt-load-more]");
    this.cardTemplate = this.querySelector("[data-fbt-card-template]");

    this.cartAddUrl = this.dataset.cartAddUrl || "/cart/add";
    this.cartUrl = this.dataset.cartUrl || "/cart";
    this.currentProductId = this.dataset.currentProductId || "";
    this.includeCurrent = this.dataset.includeCurrent === "true";
    this.ctaLabelDefault = this.dataset.ctaLabel || "In den Warenkorb legen";

    this.promoActive = this.dataset.promoActive === "true";
    this.promoVendor = this.dataset.promoVendor || "";
    this.promoThreshold = Number(this.dataset.promoThreshold) || 4;
    this.promoStep = Number(this.dataset.promoStep) || 3;
    this.promoMaxFree = Number(this.dataset.promoMaxFree) || 4;
    this.promoCardsEmpty = Number(this.dataset.promoCardsEmpty) || 4;
    this.promoCardsFilled = Number(this.dataset.promoCardsFilled) || 3;
    this.tierTopQty = (this.promoMaxFree - 1) * this.promoStep + this.promoThreshold;
    this.cartVendorQty = Number(this.dataset.cartVendorQty) || 0;

    this.autoFillMode = this.dataset.autoFillMode || "off";
    this.autoFillTarget = Number(this.dataset.autoFillTarget) || 3;
    this.autoFillFallback = this.dataset.autoFillFallback || "";

    /** Vendor items currently in cart, for accurate "free" allocation. */
    this.cartVendorItems = [];

    this.refreshCardRefs();
    this.bindCheckboxHandlers();
    this.ctaEl?.addEventListener("click", (e) => this.handleAddAll(e));
    this.loadMoreBtn?.addEventListener("click", () => this.handleLoadMore());

    if (this.promoActive) {
      this.cartUpdateHandler = this.handleCartUpdate.bind(this);
      const eventName = ThemeEvents?.cartUpdate || "cart:update";
      document.addEventListener(eventName, this.cartUpdateHandler);
      // Both fetches are independent — run in parallel so first useful paint
      // (tier track + auto-padded cards) lands sooner.
      Promise.all([this.handleCartUpdate(), this.padInitialCards()]).catch((err) =>
        console.warn("[FBT] init failed", err)
      );
    } else if (this.autoFillMode !== "off") {
      // Non-promo mode: top up the section to the configured target with
      // related/complementary recommendations.
      this.padNonPromoCards().catch((err) =>
        console.warn("[FBT] auto-fill failed", err)
      );
    }

    this.updateTotal();
  }

  disconnectedCallback() {
    if (this.promoActive && this.cartUpdateHandler) {
      const eventName = ThemeEvents?.cartUpdate || "cart:update";
      document.removeEventListener(eventName, this.cartUpdateHandler);
    }
  }

  refreshCardRefs() {
    this.checkboxes = Array.from(this.querySelectorAll("[data-fbt-checkbox]"));
    this.items = Array.from(this.querySelectorAll(".fbt-item"));
  }

  bindCheckboxHandlers() {
    this.checkboxes.forEach((cb) => {
      if (cb.dataset.fbtBound === "true") return;
      cb.addEventListener("change", () => this.updateTotal());
      cb.dataset.fbtBound = "true";
    });
  }

  /**
   * Normalize price input to integer cents. Handles:
   *  - integer cents (e.g. 1836)              → 1836
   *  - string with decimal "18.36"            → 1836
   *  - string integer "1836"                  → 1836
   *  - null/undefined/0                        → 0
   */
  parseCents(value) {
    if (value == null) return 0;
    if (typeof value === "number") return Math.round(value);
    const str = String(value);
    if (str.includes(".")) return Math.round(parseFloat(str) * 100);
    const num = Number(str);
    return Number.isFinite(num) ? Math.round(num) : 0;
  }

  formatMoney(cents) {
    return formatShopifyMoney(cents);
  }

  computeFreeItems(qty) {
    if (!this.promoActive || qty < this.promoThreshold) return 0;
    const free = Math.floor((qty - this.promoThreshold) / this.promoStep) + 1;
    return Math.min(free, this.promoMaxFree);
  }

  /**
   * Build the candidate pool of vendor-matching items (FBT checked + cart units,
   * one entry per unit), pick the cheapest N as "free", and return everything
   * downstream code needs in one pass: a Set of free FBT variant-ids (for
   * marking cards), aggregated free cart items (for the cart-free panel), and
   * the saving total in cents.
   */
  buildFreeAllocation(freeItems) {
    const empty = { freeFbtVariants: new Set(), freeCartItems: [], saving: 0 };
    if (freeItems <= 0) return empty;

    const fbtCandidates = this.checkboxes
      .filter((cb) => cb.checked && cb.dataset.vendor === this.promoVendor)
      .map((cb) => ({
        type: "fbt",
        variantId: String(cb.dataset.variantId),
        price: Number(cb.dataset.price || 0),
      }));

    const cartCandidates = (this.cartVendorItems || []).flatMap((item) => {
      // Expand by quantity so each unit is a candidate (each unit can be free).
      const qty = item.quantity || 1;
      return Array.from({ length: qty }, () => ({
        type: "cart",
        variantId: String(item.variant_id),
        price: Number(item.price || 0),
        title: item.product_title || item.title,
      }));
    });

    const winners = [...fbtCandidates, ...cartCandidates]
      .sort((a, b) => a.price - b.price)
      .slice(0, freeItems);

    const freeFbtVariants = new Set();
    const cartByVariant = new Map();
    let saving = 0;
    for (const w of winners) {
      saving += w.price;
      if (w.type === "fbt") {
        freeFbtVariants.add(w.variantId);
      } else {
        const existing = cartByVariant.get(w.variantId);
        if (existing) existing.qty += 1;
        else cartByVariant.set(w.variantId, { title: w.title, qty: 1 });
      }
    }
    const freeCartItems = Array.from(cartByVariant.entries()).map(([id, v]) => ({
      variantId: id,
      title: v.title,
      qty: v.qty,
    }));

    return { freeFbtVariants, freeCartItems, saving };
  }

  markFreeStates(freeFbtVariants) {
    this.items.forEach((li) => {
      const cb = li.querySelector("[data-fbt-checkbox]");
      const variantId = cb?.dataset.variantId;
      const isFree = variantId && freeFbtVariants.has(String(variantId));
      li.dataset.free = isFree ? "true" : "false";
    });
  }

  updateCartFreePanel(freeCartItems) {
    if (!this.cartFreeEl) return;
    if (!freeCartItems || freeCartItems.length === 0) {
      this.cartFreeEl.hidden = true;
      return;
    }
    if (this.cartFreeListEl) {
      this.cartFreeListEl.textContent = freeCartItems
        .map((c) => (c.qty > 1 ? `${c.title} (${c.qty}×)` : c.title))
        .join(", ");
    }
    this.cartFreeEl.hidden = false;
  }

  updateTierTrack(currentQty, freeItems) {
    if (this.promoFillEl) {
      const pct = Math.min(100, (currentQty / this.tierTopQty) * 100);
      this.promoFillEl.style.setProperty("--fill", `${pct.toFixed(2)}%`);
    }

    this.promoNodes.forEach((node) => {
      const qty = Number(node.dataset.tierQty || 0);
      node.dataset.active = currentQty >= qty ? "true" : "false";
    });

    if (this.promoCountEl) {
      const display = Math.min(currentQty, this.tierTopQty);
      this.promoCountEl.textContent = `${display} / ${this.tierTopQty}`;
    }

    if (!this.promoStatusEl) return;
    if (currentQty < this.promoThreshold) {
      const need = this.promoThreshold - currentQty;
      this.promoStatusEl.textContent = `Noch ${need} ${need === 1 ? "Artikel" : "Artikel"} für 1 kostenlosen Artikel.`;
    } else if (freeItems >= this.promoMaxFree) {
      this.promoStatusEl.textContent = `Maximaler Rabatt aktiv — ${freeItems} Artikel kostenlos.`;
    } else {
      const nextThreshold = this.promoThreshold + this.promoStep * freeItems;
      const need = nextThreshold - currentQty;
      this.promoStatusEl.textContent = `Stufe ${freeItems} aktiv — ${freeItems} kostenlos. Noch ${need} für ${freeItems + 1} kostenlos.`;
    }
  }

  updateTotalDisplay(grossTotal, saving) {
    if (this.totalEl) {
      const net = Math.max(0, grossTotal - saving);
      this.totalEl.textContent = this.formatMoney(net);
    }
    if (this.totalCompareEl) {
      if (saving > 0) {
        this.totalCompareEl.textContent = this.formatMoney(grossTotal);
        this.totalCompareEl.hidden = false;
      } else {
        this.totalCompareEl.hidden = true;
      }
    }
  }

  updateSavingsRow(saving) {
    if (!this.savingsEl) return;
    if (saving > 0) {
      this.savingsEl.hidden = false;
      if (this.savingsAmountEl) this.savingsAmountEl.textContent = this.formatMoney(saving);
    } else {
      this.savingsEl.hidden = true;
    }
  }

  updateCtaLabel() {
    if (!this.ctaLabelEl) return;
    this.ctaLabelEl.textContent = this.ctaLabelDefault;
  }

  updateTotal() {
    const grossTotal = this.checkboxes.reduce((sum, cb) => {
      if (!cb.checked) return sum;
      return sum + Number(cb.dataset.price || 0);
    }, 0);

    const checkedCount = this.checkboxes.filter((cb) => cb.checked).length;
    if (this.ctaEl) this.ctaEl.disabled = checkedCount === 0;

    if (!this.promoActive) {
      this.updateTotalDisplay(grossTotal, 0);
      this.updateCtaLabel();
      return;
    }

    const fbtVendorQty = this.checkboxes
      .filter((cb) => cb.checked && cb.dataset.vendor === this.promoVendor)
      .length;
    const totalVendorQty = this.cartVendorQty + fbtVendorQty;
    const freeItems = this.computeFreeItems(totalVendorQty);

    const { freeFbtVariants, freeCartItems, saving } = this.buildFreeAllocation(freeItems);

    this.markFreeStates(freeFbtVariants);
    this.updateCartFreePanel(freeCartItems);
    this.updateTierTrack(totalVendorQty, freeItems);
    this.updateTotalDisplay(grossTotal, saving);
    this.updateSavingsRow(saving);
    this.updateCtaLabel();
  }

  refreshInCartFlags(cart) {
    const cartVariantIds = new Set(cart.items.map((i) => String(i.variant_id)));
    this.items.forEach((li) => {
      const cb = li.querySelector("[data-fbt-checkbox]");
      if (!cb) return;
      const inCart = cartVariantIds.has(String(cb.dataset.variantId));
      li.dataset.inCart = inCart ? "true" : "false";
    });
  }

  async handleCartUpdate(event) {
    // Reuse cart from CartUpdateEvent.detail.resource if dispatched by another
    // theme component — avoids a redundant /cart.js fetch on every cart change.
    let cart = event?.detail?.resource;
    if (!cart) {
      try {
        const res = await fetch(`${this.cartUrl}.js`, {
          headers: { Accept: "application/json" },
        });
        if (!res.ok) return;
        cart = await res.json();
      } catch (err) {
        console.warn("[FBT] cart refresh failed", err);
        return;
      }
    }

    if (!cart || !Array.isArray(cart.items)) return;

    if (this.promoActive) {
      const vendorLines = cart.items.filter((item) => item.vendor === this.promoVendor);
      this.cartVendorQty = vendorLines.reduce((s, i) => s + i.quantity, 0);
      this.cartVendorItems = vendorLines.map((i) => ({
        variant_id: i.variant_id,
        quantity: i.quantity,
        price: i.price,
        product_title: i.product_title,
        title: i.title,
      }));
    }
    this.refreshInCartFlags(cart);
    this.updateTotal();
  }

  /**
   * How many additional vendor units must be added to advance to the next tier.
   * Returns 0 if at max tier (or promo inactive — falls back to default of 5 in caller).
   */
  computeNeededForNextTier() {
    if (!this.promoActive) return 0;
    const fbtVendorChecked = this.checkboxes.filter(
      (cb) => cb.checked && cb.dataset.vendor === this.promoVendor
    ).length;
    const totalVendorQty = this.cartVendorQty + fbtVendorChecked;

    if (totalVendorQty < this.promoThreshold) {
      return this.promoThreshold - totalVendorQty;
    }
    const free = this.computeFreeItems(totalVendorQty);
    if (free >= this.promoMaxFree) return 0;
    const nextThreshold = this.promoThreshold + this.promoStep * free;
    return Math.max(0, nextThreshold - totalVendorQty);
  }

  /**
   * Fetch related products from Shopify's recommendation API and filter them
   * against already-rendered cards + vendor + availability. Used both by
   * padInitialCards() (silent on connect) and handleLoadMore() (button click).
   */
  async fetchEligibleRecommendations(intent = "related") {
    if (!this.currentProductId) return [];
    const safeIntent = intent === "complementary" ? "complementary" : "related";
    const url = `/recommendations/products.json?product_id=${this.currentProductId}&limit=20&intent=${safeIntent}`;
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    if (!res.ok) return [];
    const data = await res.json();
    const products = Array.isArray(data.products) ? data.products : [];

    const renderedProductIds = new Set(
      this.checkboxes.map((cb) => String(cb.dataset.productId)).filter(Boolean)
    );
    const renderedVariantIds = new Set(
      this.checkboxes.map((cb) => String(cb.dataset.variantId)).filter(Boolean)
    );

    return products.filter((p) => {
      if (!p || renderedProductIds.has(String(p.id))) return false;
      const variant = p.variants?.[0];
      if (!variant) return false;
      if (renderedVariantIds.has(String(variant.id))) return false;
      if (variant.available === false) return false;
      if (this.promoActive && this.promoVendor && p.vendor !== this.promoVendor) return false;
      return true;
    });
  }

  /**
   * Auto-pad the section with vendor recommendations on connect, so the customer
   * always sees enough cards to reach the configured first tier — even when the
   * merchant has not configured fallback_products and the metafield is short.
   */
  async padInitialCards() {
    if (!this.promoActive || !this.cardTemplate || !this.listEl) return;
    if (!this.currentProductId) return;

    const targetTotal =
      this.cartVendorQty === 0 ? this.promoCardsEmpty : this.promoCardsFilled;
    const targetFbt = this.includeCurrent ? targetTotal - 1 : targetTotal;
    const currentFbt = this.items.filter(
      (li) => !li.classList.contains("fbt-item--current")
    ).length;
    const needed = targetFbt - currentFbt;
    if (needed <= 0) return;

    try {
      const eligible = await this.fetchEligibleRecommendations("related");
      const toRender = eligible.slice(0, needed);
      if (toRender.length === 0) return;
      toRender.forEach((p) => this.appendProductCard(p));
      this.refreshCardRefs();
      this.bindCheckboxHandlers();
      this.updateTotal();
    } catch (err) {
      console.warn("[FBT] initial pad failed", err);
    }
  }

  /**
   * Non-promo auto-fill: top up the section to autoFillTarget total cards
   * (including the current product if shown) using related, complementary,
   * or a smart cascade of both plus a fallback collection.
   *
   * Mode "auto" = cascade in priority order:
   *   1. Complementary (admin-configured "best matches") — highest signal
   *   2. Related (auto-derived from tags / collection) — solid fallback
   *   3. Fallback collection (e.g. Bestsellers) — guarantees we hit the target
   *
   * Each step only runs if the previous didn't fill the target. Renders
   * cards once after all sources are merged (single DOM update).
   */
  async padNonPromoCards() {
    if (this.promoActive) return;
    if (!this.cardTemplate || !this.listEl) return;
    if (!this.currentProductId) return;
    if (this.autoFillMode === "off") return;

    const targetTotal = Math.max(2, this.autoFillTarget || 3);
    const targetFbt = this.includeCurrent ? targetTotal - 1 : targetTotal;
    const currentFbt = this.items.filter(
      (li) => !li.classList.contains("fbt-item--current")
    ).length;
    const needed = targetFbt - currentFbt;
    if (needed <= 0) return;

    const collected = await this.fetchCascadeProducts(needed);
    if (collected.length === 0) return;
    collected.forEach((p) => this.appendProductCard(p));
    this.refreshCardRefs();
    this.bindCheckboxHandlers();
    this.updateTotal();
  }

  /**
   * Single source-of-truth for "give me up to N eligible products" used by
   * both the silent initial pad and the user-triggered "Mehr Produkte
   * zeigen" button. Picks the right strategy for each mode:
   *
   *   - promoActive: vendor-filtered related (Mix & Match)
   *   - auto-fill auto: complementary → related → fallback collection
   *   - auto-fill complementary / related: single source
   *   - off (load-more on a non-auto-fill section): related as default
   *
   * Each fetcher already filters out already-rendered products, so calling
   * this on top of an already-padded list returns ONLY new products.
   */
  async fetchCascadeProducts(needed) {
    if (needed <= 0) return [];
    const collected = [];
    const seenIds = new Set();
    const pushUnique = (products) => {
      for (const p of products) {
        if (collected.length >= needed) break;
        if (!p || seenIds.has(String(p.id))) continue;
        seenIds.add(String(p.id));
        collected.push(p);
      }
    };

    if (this.promoActive) {
      // Mix & Match flow keeps the existing vendor-filtered related path
      pushUnique(await this.fetchEligibleRecommendations("related"));
    } else if (this.autoFillMode === "auto") {
      // Cascade in priority order:
      //   1. Complementary — merchant-curated cross-sells (Search &
      //      Discovery app). Manual pick = highest signal.
      //   2. Related — Shopify's auto ML, primarily driven by purchase
      //      history. Real sales data, no merchant work needed.
      //   3. Fallback collection — safety net for niche / new products
      //      where neither manual nor ML data exists yet.
      pushUnique(await this.fetchEligibleRecommendations("complementary"));
      if (collected.length < needed) {
        pushUnique(await this.fetchEligibleRecommendations("related"));
      }
      if (collected.length < needed && this.autoFillFallback) {
        pushUnique(await this.fetchEligibleCollectionProducts(this.autoFillFallback));
      }
    } else if (this.autoFillMode !== "off") {
      // Single-source mode (related OR complementary)
      pushUnique(await this.fetchEligibleRecommendations(this.autoFillMode));
    } else {
      // Auto-fill off — load-more falls back to related (Shopify ML)
      pushUnique(await this.fetchEligibleRecommendations("related"));
    }

    return collected;
  }

  /**
   * Fetch products from a Shopify collection by handle, filtered for
   * availability + dedupe against already-rendered cards. Used as the
   * final cascade step in auto-fill mode when neither complementary nor
   * related recommendations could fill the target.
   */
  async fetchEligibleCollectionProducts(handle) {
    if (!handle) return [];
    const url = `/collections/${encodeURIComponent(handle)}/products.json?limit=20`;
    let res;
    try {
      res = await fetch(url, { headers: { Accept: "application/json" } });
    } catch {
      return [];
    }
    if (!res.ok) return [];
    const data = await res.json();
    const products = Array.isArray(data.products) ? data.products : [];

    const renderedProductIds = new Set(
      this.checkboxes.map((cb) => String(cb.dataset.productId)).filter(Boolean)
    );
    const renderedVariantIds = new Set(
      this.checkboxes.map((cb) => String(cb.dataset.variantId)).filter(Boolean)
    );

    return products.filter((p) => {
      if (!p || renderedProductIds.has(String(p.id))) return false;
      const variant = p.variants?.[0];
      if (!variant) return false;
      if (renderedVariantIds.has(String(variant.id))) return false;
      if (variant.available === false) return false;
      return true;
    });
  }

  async handleLoadMore() {
    if (!this.loadMoreBtn || !this.cardTemplate || !this.listEl) return;
    if (this.loadMoreBtn.dataset.loading === "true") return;

    const needed = this.promoActive
      ? this.computeNeededForNextTier()
      : 5; // Sensible default when promo inactive

    if (this.promoActive && needed === 0) {
      this.loadMoreBtn.hidden = true;
      return;
    }

    this.loadMoreBtn.dataset.loading = "true";
    this.loadMoreBtn.disabled = true;

    try {
      // Use the same cascade as the silent initial pad. The fetchers
      // dedupe against already-rendered cards, so this returns ONLY new
      // products — perfect for "Mehr Produkte zeigen".
      const collected = await this.fetchCascadeProducts(needed);
      if (collected.length === 0) {
        this.loadMoreBtn.hidden = true;
        return;
      }
      collected.forEach((p) => this.appendProductCard(p));
      this.refreshCardRefs();
      this.bindCheckboxHandlers();
      this.updateTotal();

      // Hide the load-more button when:
      //   - Promo: tier reached (no further-tier goal)
      //   - Non-promo: cascade returned fewer than requested (well dry)
      const nowNeeded = this.promoActive ? this.computeNeededForNextTier() : 1;
      if (nowNeeded === 0 || collected.length < needed) {
        this.loadMoreBtn.hidden = true;
      }
    } catch (err) {
      console.warn("[FBT] load-more failed", err);
    } finally {
      this.loadMoreBtn.dataset.loading = "false";
      this.loadMoreBtn.disabled = false;
    }
  }

  /**
   * Defense-in-depth: only accept relative paths or same-origin absolute URLs
   * for product links. Falls back to a /products/{handle} path if anything
   * looks off (e.g., a `javascript:` URL injected via a compromised API).
   */
  safeProductUrl(url, handle) {
    if (typeof url === "string") {
      if (url.startsWith("/")) return url;
      try {
        const parsed = new URL(url, location.origin);
        if (parsed.origin === location.origin) {
          return parsed.pathname + parsed.search + parsed.hash;
        }
      } catch {
        // fall through to safe fallback
      }
    }
    return handle ? `/products/${encodeURIComponent(handle)}` : "/";
  }

  /**
   * Defense-in-depth: only accept https/protocol-relative/same-origin paths
   * for image src. Rejects javascript:/data:/vbscript: URLs.
   */
  isSafeImageUrl(url) {
    if (!url || typeof url !== "string") return false;
    return url.startsWith("https://") || url.startsWith("//") || url.startsWith("/");
  }

  /**
   * Extract a usable image URL from a product object, regardless of which
   * Shopify endpoint produced it. Handles:
   *  - p.featured_image (string)        — /recommendations/products.json
   *  - p.image.src (object)             — some legacy responses
   *  - p.image (string)                 — defensive fallback
   *  - p.images[0].src (object array)   — /collections/X/products.json
   *  - p.images[0] (string array)       — defensive fallback
   * Returns the first valid URL found, or null.
   */
  extractImageUrl(p) {
    if (!p) return null;
    if (typeof p.featured_image === "string" && p.featured_image) {
      return p.featured_image;
    }
    if (p.image) {
      if (typeof p.image === "string") return p.image;
      if (typeof p.image === "object" && typeof p.image.src === "string") {
        return p.image.src;
      }
    }
    if (Array.isArray(p.images) && p.images.length > 0) {
      const first = p.images[0];
      if (typeof first === "string") return first;
      if (first && typeof first === "object" && typeof first.src === "string") {
        return first.src;
      }
    }
    return null;
  }

  appendProductCard(p) {
    const fragment = this.cardTemplate.content.cloneNode(true);
    const cb = fragment.querySelector("[data-fbt-checkbox]");
    const titleA = fragment.querySelector(".fbt-row__title--link");
    const variantSpan = fragment.querySelector(".fbt-row__variant");
    const img = fragment.querySelector(".fbt-row__image");
    const priceActual = fragment.querySelector(".fbt-row__price-actual");
    const priceCompare = fragment.querySelector(".fbt-row__price-compare");

    const variant = p.variants?.[0];
    if (!variant) return;
    // Shopify /recommendations returns prices already in cents (integer).
    // Defensive: handle both integer cents and string-with-decimal forms.
    const priceCents = this.parseCents(variant.price ?? p.price);
    const compareCents = this.parseCents(variant.compare_at_price ?? p.compare_at_price);

    if (cb) {
      cb.dataset.variantId = variant.id;
      cb.dataset.productId = p.id;
      cb.dataset.price = priceCents;
      cb.dataset.vendor = p.vendor || "";
    }
    if (titleA) {
      titleA.textContent = p.title;
      titleA.href = this.safeProductUrl(p.url, p.handle);
    }
    if (variantSpan) {
      if (variant.title && variant.title !== "Default Title") {
        variantSpan.textContent = variant.title;
        variantSpan.hidden = false;
      } else {
        variantSpan.hidden = true;
      }
    }
    // Image extraction supports both Shopify endpoints:
    //   /recommendations/products.json → p.featured_image (string URL)
    //   /collections/X/products.json   → p.images[0].src (object array)
    //   Defensive: also accept p.image.src or p.image (string).
    const imageUrl = this.extractImageUrl(p);
    if (img && this.isSafeImageUrl(imageUrl)) {
      img.src = imageUrl;
      img.alt = p.title;
    } else if (img) {
      img.remove();
    }
    if (priceActual) priceActual.textContent = this.formatMoney(priceCents);
    if (priceCompare) {
      if (compareCents > priceCents) {
        priceCompare.textContent = this.formatMoney(compareCents);
        priceCompare.hidden = false;
      } else {
        priceCompare.hidden = true;
      }
    }
    this.listEl.appendChild(fragment);
  }

  hideError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = "";
  }

  showError(msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg;
    this.errorEl.hidden = false;
  }

  setLoading(isLoading) {
    if (!this.ctaEl) return;
    this.ctaEl.dataset.loading = isLoading ? "true" : "false";
    this.ctaEl.disabled = isLoading;
  }

  async handleAddAll(event) {
    event.preventDefault();
    this.hideError();

    const items = this.checkboxes
      .filter((cb) => cb.checked && cb.dataset.variantId)
      .map((cb) => ({ id: Number(cb.dataset.variantId), quantity: 1 }));

    if (items.length === 0) {
      this.showError("Bitte mindestens ein Produkt auswählen.");
      return;
    }

    this.setLoading(true);

    try {
      const addUrl = this.cartAddUrl.endsWith(".js") ? this.cartAddUrl : `${this.cartAddUrl}.js`;
      const res = await fetch(addUrl, fetchConfig("json", { body: JSON.stringify({ items }) }));
      const data = await res.json();

      if (!res.ok || data.status) {
        const msg = data.description || data.message || "Hinzufügen fehlgeschlagen.";
        this.showError(msg);
        return;
      }

      const cartRes = await fetch(`${this.cartUrl}.js`, {
        headers: { Accept: "application/json" },
      });
      const cartJson = await cartRes.json();

      this.dispatchEvent(
        new CartUpdateEvent(cartJson, this.dataset.sectionId || "fbt", {
          source: "frequently-bought-together",
          itemCount: cartJson.item_count || 0,
        })
      );

      const cartType = window.FoxTheme?.settings?.cartType;
      if (cartType === "drawer") {
        document.dispatchEvent(new CustomEvent(ThemeEvents.cartDrawerOpen || "cart-drawer:open"));
        const drawer = document.querySelector("cart-drawer-component, cart-drawer");
        if (drawer && typeof drawer.show === "function") drawer.show();
      } else {
        window.location = this.cartUrl;
      }
    } catch (err) {
      console.error("[FBT] add-all error", err);
      this.showError("Netzwerkfehler. Bitte erneut versuchen.");
    } finally {
      this.setLoading(false);
    }
  }
}

if (!customElements.get("fbt-bundle")) {
  customElements.define("fbt-bundle", FbtBundle);
}
